import Controller from '@ember/controller'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { isEmpty } from '@ember/utils'
import { action } from '@ember/object'
import { ThemeKeys, subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { buildDataOperationsConfigProxy } from 'teacher-ui-client/components/data-operations/data-operations-proxy'
import { SortType, SortDirection } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import { AllClassesOption } from 'teacher-ui-client/components/base/select/school-class/options'
import { AllTeachersOption } from 'teacher-ui-client/components/base/select/teacher/options'
import { AllGradesOption } from 'teacher-ui-client/components/base/select/grade-position/options'
import type { IntlService } from 'ember-intl'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type SessionService from 'teacher-ui-client/services/session'
import type { MoveStudentsModel } from 'teacher-ui-client/manage/move-students/route'
import type Student from 'teacher-ui-client/models/student'
import { hasId } from 'teacher-ui-client/utils/type-guards'

const ALLOCATION_STATUS = {
  /** show ALL students regardless of allocation status. */
  ALL: 'ALL',
  /** only shows students WITH a teacher/class. */
  ALLOCATED: 'ALLOCATED',
  /** only shows students WITHOUT a teacher/class. */
  UNALLOCATED: 'UNALLOCATED',
}

export default class MoveStudentsController extends Controller {
  @service intl!: IntlService
  @service gradeSets!: GradeSetsService
  @service session!: SessionService

  declare model: MoveStudentsModel

  queryParams = [
    { sortCol: 'col' },
    { sortDir: 'dir' },
    { currentPage: 'pageNumber' },
    { textFilter: 'filter' },
    'selected',
    { schoolClassesFilterValue: 'class' },
    { teachersFilterValue: 'teacher' },
    { gradesFilterValue: 'grade' },
    { allocationFilterValue: 'allocation' },
    { recordsPerPage: 'perPage' },
  ] as const

  @tracked sortCol = 'firstName'
  @tracked sortDir = SortDirection.Asc
  @tracked sortCompare = SortType.ByValue
  @tracked currentPage = 1
  @tracked recordsPerPage = 100
  @tracked selected = ''
  @tracked textFilter = ''

  get dataOperationsConfig() {
    return buildDataOperationsConfigProxy(this)
  }

  get themeKey() {
    return subscriptionTypeToThemeKey(this.model.subscriptionType)
  }

  get themeClasses() {
    const themes: Record<
      typeof this.themeKey,
      {
        actionsHeaderBg: string
        tableHeaderBg: string
        tableRowBg: string
      }
    > = {
      [ThemeKeys.blue]: {
        actionsHeaderBg: 'bg-oceany-blue-150',
        tableHeaderBg: 'bg-oceany-blue-100',
        tableRowBg: 'hover:bg-oceany-blue-100 even:bg-oceany-blue-50',
      },
      [ThemeKeys.green]: {
        actionsHeaderBg: 'bg-ms-green-150',
        tableHeaderBg: 'bg-ms-green-100',
        tableRowBg: 'hover:bg-ms-green-100 even:bg-ms-green-50',
      },
      [ThemeKeys.darkBlue]: {
        actionsHeaderBg: 'bg-wl-blue-100',
        tableHeaderBg: 'bg-wl-blue-100',
        tableRowBg: 'hover:bg-wl-blue-100 even:bg-wl-blue-50',
      },
      [ThemeKeys.default]: {
        actionsHeaderBg: 'bg-dusty-black-100',
        tableHeaderBg: 'bg-dusty-black-50',
        tableRowBg: 'group-hover:bg-dusty-black-50',
      },
    }
    return themes[this.themeKey]
  }

  get selectedIds() {
    const isDigits = (str: string) =>
      // ensure string is a positive integer (and checks for empty str '')
      /^\d+$/.test(str)
    return this.selected.split(',').filter(isDigits).map(String)
  }

  set selectedIds(value) {
    this.selected = value.join(',')
  }

  @tracked allocationFilterValue = ALLOCATION_STATUS.ALL
  @tracked schoolClassesFilterValue = AllClassesOption.id
  @tracked gradesFilterValue = AllGradesOption.id
  @tracked teachersFilterValue = AllTeachersOption.id

  get subscriptionType() {
    return this.model.subscriptionType
  }
  get students() {
    return this.model.students
  }
  get mySchoolClasses() {
    return this.model.teacher.schoolClasses
  }
  get allSchoolClasses() {
    return this.model.schoolClasses
  }

  /**
   * Computes which key to use on the student model when accessing the student's school classes.
   */
  get schoolClassNamesKey() {
    switch (this.subscriptionType) {
      case SubscriptionType.Reading:
        return 'readingSchoolClassNames'
      case SubscriptionType.Maths:
        return 'mathsSchoolClassNames'
      case SubscriptionType.Writing:
        return 'writingSchoolClassNames'
    }
  }

  /**
   * Computes which key to use on the student model when accessing the student's teacher names.
   */
  get teacherNamesKey() {
    switch (this.subscriptionType) {
      case SubscriptionType.Reading:
        return 'readingTeacherNames'
      case SubscriptionType.Maths:
        return 'mathsTeacherNames'
      case SubscriptionType.Writing:
        return 'writingTeacherNames'
    }
  }

  get allocationOptionSet() {
    return {
      id: 'allocation-filter',
      options: [
        {
          id: ALLOCATION_STATUS.UNALLOCATED,
          label: this.intl.t('manage.manageClasses.students.unallocated'),
        },
        {
          id: ALLOCATION_STATUS.ALLOCATED,
          label: this.intl.t('manage.manageClasses.students.allocated'),
        },
        {
          id: ALLOCATION_STATUS.ALL,
          label: this.intl.t('manage.selectAllLabel'),
        },
      ],
    }
  }

  get allSchoolClassesForSubscriptionType() {
    return this.allSchoolClasses.filter((schoolClass) => this.subscriptionType === schoolClass.subscriptionType)
  }

  get schoolClassesForSelect() {
    return this.allSchoolClassesForSubscriptionType.sort((a, b) => a.name.localeCompare(b.name))
  }

  get teachersForSubscriptionType() {
    return this.model.teachers.filter((teacher) => teacher.hasSchoolClassForSubscriptionType(this.subscriptionType))
  }

  get teachersForSelect() {
    return this.teachersForSubscriptionType.sort((a, b) => a.fullName.localeCompare(b.fullName))
  }

  get gradePositionsForSelect() {
    const gradePositions = this.students.reduce(
      (gradePositions, student) => gradePositions.add(student.gradePosition),
      new Set<number>(),
    )
    return [...gradePositions].sort((a, b) => a - b)
  }

  get isSubco() {
    return this.session.currentTeacher.subcoFor(this.subscriptionType)
  }

  get addToClassTextKey() {
    return `manage.manageClasses.students.bulkActions.addToClass.${this.isSubco ? 'titleSubco' : 'title'}`
  }

  AllClassesOption = AllClassesOption

  AllTeachersOption = AllTeachersOption

  AllGradesOption = AllGradesOption

  isSelected(student: Student) {
    const studentId = String(student.id)
    return studentId ? this.selectedIds.includes(studentId) : false
  }

  schoolClassesFilterFunction(student: Student) {
    const studentClassIds = student.schoolClassesFor(this.subscriptionType).map((schoolClass) => schoolClass.id)
    return (
      this.schoolClassesFilterValue === AllClassesOption.id ||
      this.isSelected(student) ||
      studentClassIds.includes(this.schoolClassesFilterValue)
    )
  }

  teachersFilterFunction(student: Student) {
    const teacherIds = student.teachersFor(this.subscriptionType).map((teacher) => teacher.id)
    return (
      this.teachersFilterValue === AllTeachersOption.id ||
      this.isSelected(student) ||
      teacherIds.includes(this.teachersFilterValue)
    )
  }

  gradesFilterFunction(student: Student) {
    return (
      this.isSelected(student) ||
      this.gradesFilterValue === AllGradesOption.id ||
      student.gradePosition === this.gradesFilterValue
    )
  }

  get recordsFilteredByAllocation() {
    if (this.allocationFilterValue === ALLOCATION_STATUS.ALL) {
      return this.students
    }
    const showAllocated = this.allocationFilterValue === ALLOCATION_STATUS.ALLOCATED
    return this.students.filter((student) => {
      const studentIsSelected = this.isSelected(student)
      const studentIsAllocated = student.isAllocatedForSubscriptionType(this.subscriptionType)
      const satisfiesAllocationFilter = showAllocated === studentIsAllocated
      const shouldDisplayRecord = studentIsSelected || satisfiesAllocationFilter
      return shouldDisplayRecord
    })
  }

  get recordsFilteredByGroup() {
    return this.recordsFilteredByAllocation
      .filter(hasId)
      .filter(
        (student) =>
          this.schoolClassesFilterFunction(student) &&
          this.teachersFilterFunction(student) &&
          this.gradesFilterFunction(student),
      )
  }

  get data() {
    const textFilter = this.textFilter || ''
    const trimmedTextFilter = textFilter.trim()
    // if no filters are applied, return all data
    if (isEmpty(trimmedTextFilter)) return this.recordsFilteredByGroup

    return this.recordsFilteredByGroup.filter((student) => {
      // if it's selected, keep it even if it doesn't match the filter
      if (student.id && this.selectedIds.includes(String(student.id))) return true

      const grade = this.gradeSets.findByPosition(student.gradePosition)

      const content = [
        student.firstName,
        student.lastName,
        student.login,
        student
          .schoolClassesFor(this.model.subscriptionType)
          .map((item) => item.name)
          .join(' '),
        student
          .teachersFor(this.model.subscriptionType)
          .map((t) => t.fullName)
          .join(' '),
        grade.fullName,
      ].join(' ')

      return content.toLowerCase().includes(trimmedTextFilter.toLowerCase())
    })
  }

  @tracked addToClassModalOpen = false

  @action
  onSearchInput(value: string) {
    this.textFilter = value
    this.currentPage = 1
  }

  @action
  showAddToClassModal(show: boolean) {
    this.addToClassModalOpen = show
  }

  /**
   * The Show `All | `Allocated` | `Unallocated` filter button group has been toggled.
   * @param {ALLOCATION_STATUS} value
   */
  @action
  updateAllocationFilterValue(_optionSet: unknown, value: string) {
    this.selectedIds = []
    this.allocationFilterValue = value
    if (value === ALLOCATION_STATUS.UNALLOCATED) {
      // Unassigned students don't have a school class or teacher hence the school class and teacher filters are
      // reset when the `Show Unallocated` filter is activated.
      this.schoolClassesFilterValue = AllClassesOption.id
      this.teachersFilterValue = AllTeachersOption.id
    }
  }

  @action
  updateSchoolClassId(schoolClassId: string) {
    this.schoolClassesFilterValue = schoolClassId
  }

  @action
  updateTeacherId(teacherId: string) {
    this.teachersFilterValue = teacherId
  }

  @action
  updateGradePosition(gradePosition: number) {
    this.gradesFilterValue = gradePosition
  }

  @action
  studentsAddedToClass() {
    this.showAddToClassModal(false)
    this.selectedIds = []
  }
}
